import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NewImportExportService } from '../../../_service/new-import-export.service';
import { SigninService } from 'src/app/authentification/signin/signin.service';
import { User } from 'src/app/authentification/signin/credentials';

@Component({
  selector: 'app-stop-no-poi-daily-excel',
  templateUrl: './stop-no-poi-daily-excel.component.html',
  styleUrls: ['./stop-no-poi-daily-excel.component.css']
})
export class StopNoPoiDailyExcelComponent implements OnInit, OnDestroy {
  excelData: any[] = [];
  excelData$: Subscription;
  current: string = '';
  public currentUser: User = new User();
  currentSheet: number = 0;
  sheetNames: string[] = [];

  processedSheets: Map<number, any[]> = new Map();
  filteredDataCache: Map<number, any[]> = new Map();
  headerRowIndexCache: Map<number, number> = new Map();

  private readonly COORDINATE_COLUMNS = ['Coordonnées', 'coordonnées', 'COORDONNEES', 'Coordonnees'];
  private readonly DURATION_COLUMNS = ['Temps d\'arrêt', 'T.A', 'Dépassement', 'T.A (min)'];
  private readonly DAY_FRACTION_DURATION_COLUMNS = ['Temps d\'arrêt', 'Dépassement'];

  constructor(
    private importExportService: NewImportExportService,
    private authService: SigninService
  ) {
    const storedUser = localStorage.getItem('currentUser');
    this.currentUser = storedUser ? JSON.parse(storedUser) : {};
  }

  ngOnInit(): void {
    this.fetchDataAndProcess();
    this.fetchUser();
  }

  ngOnDestroy(): void {
    if (this.excelData$) {
      this.excelData$.unsubscribe();
    }
    this.importExportService.setExcelData(null);
    this.importExportService.setCurentRapport(null);
    this.importExportService.setPdfToBePrinted(null);
    this.importExportService.setExcelToBeProcessed(null);
    this.clearAllCaches();
  }

  isDayFractionDurationColumn(header: string): boolean {
    if (!header) return false;
    return this.DAY_FRACTION_DURATION_COLUMNS.some(col =>
      header.trim().toLowerCase() === col.toLowerCase()
    );
  }

  fetchUser(): void {
    this.current = this.authService.getCurrentUser().displayName;
  }

  private normalizeExcelSheets(data: any[] | null): any[] {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    if (Array.isArray(data[0])) {
      return [{ sheetName: this.getSingleSheetName(data), data: data }];
    }

    if (data[0] && typeof data[0] === 'object' && 'data' in data[0]) {
      return data;
    }

    return [{ sheetName: this.getSingleSheetName(data), data: data }];
  }

  private getSingleSheetName(data: any[] | null): string {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return 'Feuille 1';
    }

    const candidate = data[0];
    if (candidate && typeof candidate === 'object' && 'sheetName' in candidate && candidate.sheetName) {
      return candidate.sheetName;
    }

    return 'Feuille 1';
  }

  fetchDataAndProcess(): void {
    this.excelData$ = this.importExportService
      .getExcelData()
      .subscribe((data) => {
        const normalizedData = this.normalizeExcelSheets(data as any[] | null);
        if (normalizedData.length > 0) {
          this.excelData = normalizedData;
          this.sheetNames = this.excelData.map((sheet) => sheet.sheetName || 'Feuille 1');
          this.currentSheet = 0;
          this.clearAllCaches();
        }
      });
  }

  clearAllCaches(): void {
    this.processedSheets.clear();
    this.filteredDataCache.clear();
    this.headerRowIndexCache.clear();
  }

  selectSheet(index: number): void {
    if (this.currentSheet === index) {
      this.processedSheets.delete(index);
      this.filteredDataCache.delete(index);
      this.headerRowIndexCache.delete(index);
    }
    this.currentSheet = index;
  }

  fillEmptyCells(row: any[], columnCount: number): any[] {
    while (row.length < columnCount) {
      row.push('');
    }
    return row;
  }

  // Rapport journalier : ligne contenant à la fois 'Véhicule' et 'Date début'
  private getHeaderRowIndex(sheetData: any[]): number {
    if (!sheetData || sheetData.length === 0) {
      return 3;
    }

    const dailyIdx = sheetData.findIndex(
      (row: any[]) =>
        Array.isArray(row) &&
        row.some(
          (cell: any) =>
            cell !== null && cell !== undefined && cell.toString().trim() === 'Véhicule'
        ) &&
        row.some(
          (cell: any) =>
            cell !== null && cell !== undefined && cell.toString().trim() === 'Date début'
        )
    );

    return dailyIdx >= 0 ? dailyIdx : 3;
  }

  isCoordinateColumn(header: string): boolean {
    if (!header) return false;
    return this.COORDINATE_COLUMNS.some(col =>
      header.trim().toLowerCase() === col.toLowerCase()
    );
  }

  isDurationColumn(header: string): boolean {
    if (!header) return false;
    return this.DURATION_COLUMNS.some(col =>
      header.trim().toLowerCase() === col.toLowerCase()
    );
  }

  isCoordsColumn(header: string): boolean {
  return header === 'Coordonnées' || header === 'LatLng';
}

  columnLetterToIndex(letter: string): number {
    let index = 0;
    for (let i = 0; i < letter.length; i++) {
      index = index * 26 + (letter.charCodeAt(i) - 64);
    }
    return index - 1;
  }

  getColumnIndexByName(headers: any[], columnName: string): number {
    return headers.findIndex((h: any) => h && h.toString().trim() === columnName);
  }

  formatDurationAsDaysHMS(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'string' && value.startsWith('=')) {
      return value;
    }

    const decimalDays = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(decimalDays)) {
      return value?.toString() || '';
    }

    const totalSeconds = Math.round(decimalDays * 24 * 60 * 60);

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  evaluateExcelFormula(value: any, rowData: any[], allData: any[], headerRowIndex: number, rowIndex: number): any {
    if (typeof value !== 'string' || !value.startsWith('=')) {
      return value;
    }

    try {
      const formula = value.trim();

      if (formula.includes('SUM(')) {
        const match = formula.match(/SUM\(([A-Z]+)(\d+):([A-Z]+)(\d+)\)/);
        if (match) {
          const startRow = parseInt(match[2]);
          const endRow = parseInt(match[4]);
          const colLetter = match[1];
          const colIndex = this.columnLetterToIndex(colLetter);

          let sum = 0;
          for (let i = startRow; i <= endRow; i++) {
            const rowIdx = i - 1;
            if (allData[rowIdx] && allData[rowIdx][colIndex] !== undefined) {
              const val = parseFloat(allData[rowIdx][colIndex]);
              if (!isNaN(val)) {
                sum += val;
              }
            }
          }

          if (formula.includes('/(24*60)')) {
            const result = sum / (24 * 60);
            return typeof result === 'number' ? parseFloat(result.toFixed(2)) : value;
          }
          if (formula.includes('/60')) {
            const result = sum / 60;
            return typeof result === 'number' ? parseFloat(result.toFixed(2)) : value;
          }
          return sum;
        }
        return value;
      }

      const simpleDivMatch = formula.match(/=([A-Z]+)(\d+)\/(\d+(?:\*\d+)?)/);
      if (simpleDivMatch) {
        const colLetter = simpleDivMatch[1];
        const rowNum = parseInt(simpleDivMatch[2]);
        const divisorStr = simpleDivMatch[3];
        const divisor = divisorStr.includes('*') ? eval(divisorStr) : parseInt(divisorStr);
        const colIndex = this.columnLetterToIndex(colLetter);

        const rowIdx = rowNum - 1;
        if (allData[rowIdx] && allData[rowIdx][colIndex] !== undefined) {
          let val = allData[rowIdx][colIndex];
          if (typeof val === 'string' && val.startsWith('=')) {
            val = this.evaluateExcelFormula(val, allData[rowIdx], allData, headerRowIndex, rowIdx);
          }
          const numVal = parseFloat(val);
          if (!isNaN(numVal) && divisor > 0) {
            const result = numVal / divisor;
            return typeof result === 'number' ? parseFloat(result.toFixed(2)) : value;
          }
        }
        return value;
      }

      const subDivMatch = formula.match(/=\(([A-Z]+)(\d+) - (\d+)\)\/(\d+(?:\*\d+)?)/);
      if (subDivMatch) {
        const colLetter = subDivMatch[1];
        const rowNum = parseInt(subDivMatch[2]);
        const subtract = parseInt(subDivMatch[3]);
        const divisorStr = subDivMatch[4];
        const divisor = divisorStr.includes('*') ? eval(divisorStr) : parseInt(divisorStr);
        const colIndex = this.columnLetterToIndex(colLetter);

        const rowIdx = rowNum - 1;
        if (allData[rowIdx] && allData[rowIdx][colIndex] !== undefined) {
          let val = allData[rowIdx][colIndex];
          if (typeof val === 'string' && val.startsWith('=')) {
            val = this.evaluateExcelFormula(val, allData[rowIdx], allData, headerRowIndex, rowIdx);
          }
          const numVal = parseFloat(val);
          if (!isNaN(numVal) && divisor > 0) {
            const result = (numVal - subtract) / divisor;
            return typeof result === 'number' ? parseFloat(result.toFixed(2)) : value;
          }
        }
        return value;
      }

      return value;
    } catch (e) {
      console.warn('Error evaluating formula:', value, e);
      return value;
    }
  }

  formatDuration(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'string' && value.startsWith('=')) {
      return value;
    }

    let num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) {
      return value?.toString() || '';
    }

    return num.toFixed(2);
  }

  extractCoordinates(value: any): { lat: string, lng: string } | null {
    if (!value || value === '') return null;

    const str = value.toString().trim();
    const parts = str.split(';');
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat: lat.toString(), lng: lng.toString() };
      }
    }
    return null;
  }

  getGoogleMapsLink(coordinates: string): string {
    if (!coordinates) return '#';
    const coords = this.extractCoordinates(coordinates);
    if (coords) {
      return `http://maps.google.com/?q=${coords.lat},${coords.lng}`;
    }
    return '#';
  }

  hasValidCoordinates(value: any): boolean {
    return this.extractCoordinates(value) !== null;
  }

  getTitleInfo(): { title: string, info: string } {
    const sheetData = this.getActiveSheetData();
    if (sheetData.length === 0) {
      return { title: '', info: '' };
    }

    const titleRow = sheetData[0] || [];
    return {
      title: titleRow[2] || 'Rapport journalier des arrêts par non poi',
      info: titleRow[4] || ''
    };
  }

  getActiveSheetData(): any[] {
    if (this.currentSheet >= this.excelData.length) {
      return [];
    }

    if (this.processedSheets.has(this.currentSheet)) {
      return this.processedSheets.get(this.currentSheet) || [];
    }

    const sheetData = this.excelData[this.currentSheet]?.data || [];
    if (sheetData.length === 0) return [];

    const headerRowIndex = this.getHeaderRowIndex(sheetData);
    const columnCount = Math.max(
      ...sheetData.map((row: any[]) => row?.length || 0)
    );

    if (columnCount === 0) {
      return [];
    }

    const rawData = sheetData.slice(0);

    let evaluated = false;
    let iterations = 0;
    const maxIterations = 10;

    while (!evaluated && iterations < maxIterations) {
      evaluated = true;
      iterations++;

      for (let i = 0; i < rawData.length; i++) {
        const row = rawData[i];
        if (!row) continue;

        for (let j = 0; j < row.length; j++) {
          const cell = row[j];
          if (typeof cell === 'string' && cell.startsWith('=')) {
            const newValue = this.evaluateExcelFormula(cell, row, rawData, headerRowIndex, i);
            if (newValue !== cell) {
              rawData[i][j] = newValue;
              evaluated = false;
            }
          }
        }
      }
    }

    const result = rawData.map((row: any[]) => {
      return this.fillEmptyCells(row || [], columnCount);
    });

    (result as any).headerRowIndex = headerRowIndex;

    this.processedSheets.set(this.currentSheet, result);
    this.headerRowIndexCache.set(this.currentSheet, headerRowIndex);

    return result;
  }

  getFilteredData(): any[] {
    if (this.filteredDataCache.has(this.currentSheet)) {
      return this.filteredDataCache.get(this.currentSheet) || [];
    }

    const activeData = this.getActiveSheetData();
    const headerRowIndex = this.headerRowIndexCache.get(this.currentSheet) ?? 3;

    if (activeData.length === 0) return [];

    const filtered = activeData
      .slice(headerRowIndex + 1)
      .filter((row: any[]) =>
        row.some(
          (cell: any) =>
            cell !== null && cell !== undefined && cell.toString().trim() !== ''
        )
      );

    this.filteredDataCache.set(this.currentSheet, filtered);
    return filtered;
  }

  isTotalRow(row: any[]): boolean {
    if (!row || row.length === 0) return false;
    return row.some((cell: any) =>
      cell && typeof cell === 'string' &&
      (cell.toLowerCase().includes('total') && !cell.toLowerCase().includes('total (heure)'))
    );
  }

  isTotalHoursRow(row: any[]): boolean {
    if (!row || row.length === 0) return false;
    return row.some((cell: any) =>
      cell && typeof cell === 'string' && cell.toLowerCase().includes('total (heure)')
    );
  }

  getRow(rowIndex: number): any[] {
    const sheetData = this.getActiveSheetData();
    return sheetData[rowIndex] || [];
  }

  getHeaders(): any[] {
    const sheetData = this.getActiveSheetData();
    const headerRowIndex = this.headerRowIndexCache.get(this.currentSheet) ?? 3;
    return sheetData[headerRowIndex] || [];
  }

  getRowClass(row: any[]): string {
    if (this.isTotalHoursRow(row)) {
      return 'total-hours-row';
    }
    if (this.isTotalRow(row)) {
      return 'total-row';
    }
    return '';
  }

  getCellValue(row: any[], index: number, header: string): string {
    if (!row || index >= row.length) {
      return '';
    }

    const value = row[index];
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (this.isTotalRow(row) || this.isTotalHoursRow(row)) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num.toFixed(2);
      }
      return value?.toString() || '';
    }

    if (header === 'T.A (min)') {
      return this.formatDuration(value);
    }

    return value?.toString() || '';
  }

  isMapLink(header: string, value: any): boolean {
    return header === 'Coordonnées' && this.hasValidCoordinates(value);
  }

  refreshCurrentSheet(): void {
    const index = this.currentSheet;
    this.processedSheets.delete(index);
    this.filteredDataCache.delete(index);
    this.headerRowIndexCache.delete(index);
  }
}